import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { axiosInstance } from "../../utils/axiosInstance";
export interface CreateApplicatiomState {
  appCode: string;
  description: string;
  primaryContact: string;
  businessOwner: string;
  applicationName: string;
  appGuid: string;
  protectedBy: any;
  domain_id?: any;
  value_stream_id?: any;
  businessOwnerName: string;
  primaryContactName: string;
  azure_ad_name?: string;
}

export const createApplication = createAsyncThunk<
  CreateApplicatiomState, // Define the return type explicitly
  CreateApplicatiomState // Define the argument type
>("createApplication/fetchData", async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post("/appsetup/application", data);
    return response?.data as CreateApplicatiomState;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to create application",
    );
  }
});

export const editApplication = createAsyncThunk(
  "editApplication/fetchData",
  async (data: any, { rejectWithValue }) => {
    try {
      const hasApplicationApproverPayload =
        !!data?.applicationApproverPayload &&
        Object.keys(data.applicationApproverPayload).length > 0;
      const selectedApprover = (data?.approver ?? "")
        .toString()
        .trim()
        .toLowerCase();
      const payloadGroupName = (data?.applicationApproverPayload?.group_name ?? "")
        .toString()
        .trim()
        .toLowerCase();

      // Only treat approver as payload-based when the selected approver matches payload group name.
      // This prevents stale payload from forcing applicationApprover to null for existing groups.
      const useApplicationApproverPayload =
        hasApplicationApproverPayload &&
        selectedApprover !== "" &&
        payloadGroupName !== "" &&
        selectedApprover === payloadGroupName;

      const response = await axiosInstance.put("/appsetup/application", {
        id: data?.id ?? "",
        appCode: data?.appCode ?? "",
        description: data?.description ?? "",
        primaryContact: data?.primaryContact ?? "",
        primaryContactName: data?.primaryContactName ?? "",
        businessOwner: data?.businessOwner ?? "",
        businessOwnerName: data?.businessOwnerName ?? "",
        applicationName: data?.applicationName ?? "",
        appGuid: data?.appGuid ?? "",
        protectedBy: data?.protectedBy,
        domain_id: data?.domain_id,
        value_stream_id: data?.value_stream_id,
        applicationAdmin: data?.appadmin,
        azure_ad_name: data?.azure_ad_name ?? "",
        applicationApprover: useApplicationApproverPayload
          ? null
          : data?.approver,
        applicationApproverName: data?.applicationApproverName,
        publishApprover: data?.publishApprover,
        ...(useApplicationApproverPayload && {
            applicationApproverPayload: data.applicationApproverPayload,
          }),
      });
      return response.data as CreateApplicatiomState;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update application",
      );
    }
  },
);

export interface initialState {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  data: any | null;
  editStatus: "idle" | "loading" | "succeeded" | "failed";
  editError: string | null;
  editData: any | null;
  env?: string;
}

const initialState: initialState = {
  status: "idle",
  data: null,
  error: null,
  editStatus: "idle",
  editData: null,
  editError: null,
  env: "",
};

const createApplicationSlice = createSlice({
  name: "createApplication",
  initialState: initialState,
  reducers: {
    resetCreateApplication: (state) => {
      state.status = "idle";
      state.error = null;
    },
    resetEditApplication: (state) => {
      state.editStatus = "idle";
      state.editError = "";
      state.editData = null;
    },
    updateCreateApplication: (
      state,
      action: PayloadAction<CreateApplicatiomState>,
    ) => {
      state.data = action.payload;
    },
    updateEnv: (state, action: PayloadAction<string>) => {
      state.env = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createApplication.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        createApplication.fulfilled,
        (state, action: PayloadAction<CreateApplicatiomState>) => {
          state.status = "succeeded";
          state.data = action.payload;
        },
      )
      .addCase(createApplication.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch data";
      })
      .addCase(editApplication.pending, (state) => {
        state.editStatus = "loading";
      })
      .addCase(
        editApplication.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.editStatus = "succeeded";
          state.editData = action.payload;
        },
      )
      .addCase(editApplication.rejected, (state, action) => {
        state.editStatus = "failed";
        state.editError = action.error.message || "Failed to fetch data";
      });
  },
});

export const {
  resetCreateApplication,
  resetEditApplication,
  updateCreateApplication,
  updateEnv,
} = createApplicationSlice.actions;
export const selectCreateApplication = (state: RootState) =>
  state.createApplication;
export default createApplicationSlice.reducer;
